/**
 * ParentDocsPage.js — Parent documentation: full articles written for
 * parents/guardians, readable in-page. No thin link-out cards.
 */
import React, { useState } from 'react';
import { BookOpen, FileText, ShieldCheck, Heart, Wallet, Crown, LifeBuoy, Rocket, ChevronDown } from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';

const DOCS = [
  {
    id: 'getting-started', icon: Rocket, title: 'Getting Started as a Parent',
    body: [
      'Signing in: your dashboard uses no password. You register once, scan the QR code we show you with Google Authenticator (or Authy/1Password) on your phone, and from then on you sign in with your email plus the 6-digit code your app shows. The code rotates every 30 seconds — that’s what keeps your family’s data safe.',
      'Your role on HiBFF is simple: sign the Commitment Charter, then step back and let your teen practise.',
      'After you sign, your teen receives their invitation to co-sign. Once both signatures are in, their account is fully unlocked and you’ll see "Charter Aligned" on your dashboard.',
      'Everything you need lives in this dashboard: Hearts oversight, the 180-day Vault, progress tracking, and direct support. You never need to touch your teen’s account — and by design, you can’t.',
    ],
  },
  {
    id: 'charter', icon: FileText, title: 'The Commitment Charter',
    body: [
      'The Charter is a real, signed agreement — not a formality. You make four unequivocal promises: Complete Privacy, Unconditional Support, Full Ownership of their Rewards, and Consistency Not Grades.',
      'You sign first. That matters: it shows your teen the trust flows their way before anything is asked of them. They co-sign only when they’re ready.',
      'Your teen can read the signed Charter any time from their own dashboard (Family → Charter Doc), including a plain-language explanation of what it means for each of you.',
    ],
  },
  {
    id: 'privacy', icon: ShieldCheck, title: 'Privacy Shield — what you can and can’t see',
    body: [
      'You CAN see: progress, streaks, badges, Hearts, challenges completed, Vault items, and how often your teen practises.',
      'You CANNOT see: a single word of their conversations. Chat logs are blocked from every parent-facing endpoint with a hard server-side 403 — this is enforced in code, not policy.',
      'Personal details are redacted before anything leaves the device, and practice sessions are auto-deleted on a strict rolling window. Nine safety domains are actively filtered in real time, and genuine safety concerns are escalated by our team — that is the one exception to privacy, and it goes through humans, never automated snooping.',
    ],
  },
  {
    id: 'hearts', icon: Heart, title: 'Hearts, explained',
    body: [
      'Hearts are HiBFF’s encouragement currency. Teens earn them by showing up: completing challenges, keeping streaks, signing the Charter, and practising with their AvA tutors.',
      'Hearts are spent in the Hearts Store on practice credits, streak freezes and collectibles. Per the Charter, Hearts belong to your teen — they can never be confiscated.',
      'Big-ticket spends route to you for approval. You’ll see them waiting on your Hearts page — approve or decline with one tap.',
    ],
  },
  {
    id: 'vault', icon: Wallet, title: 'The 180-Day Vault & the NFT pivot',
    body: [
      'Every badge, trophy and collectible your teen earns is locked in their Secure Vault for 180 days. The wait is deliberate: it turns quick rewards into something owned and anticipated.',
      'The pivot point: once your teen has earned enough virtual badges, their best collectibles can be minted as real NFTs on Polygon via Crossmint. You’ll receive a text the moment their first mint happens — it’s a milestone worth celebrating at the dinner table.',
      'When the 180-day clock reaches zero, the full artwork is revealed. Your teen chooses: keep it forever, or trade it — with 100% of proceeds routed to their in-app wallet.',
    ],
  },
  {
    id: 'safety', icon: LifeBuoy, title: 'Safety & escalation',
    body: [
      'Every AvA tutor is topic-locked with deterministic guards — they cannot be steered off their specialist subject, and nine safety domains are filtered before any AI ever responds.',
      'If your teen ever needs a human, the "Maya Helps" button in their Help & Support sends a message straight to our team, answered personally. Their dashboard also carries a Safety Net with local emergency numbers.',
      'If our team ever identifies a genuine safety concern, we contact you directly. You’ll never be left out of something that matters.',
    ],
  },
  {
    id: 'billing', icon: Crown, title: 'Plans & billing',
    body: [
      'Plans are billed to you, never to your teen. Upgrade, pause or cancel any time from the Upgrade Plan page — no lock-in, no cancellation calls.',
      'If you cancel, your teen’s earned rewards, badges and Vault items are preserved — they own those regardless of your subscription.',
      'Receipts and plan changes are emailed to you. Anything unclear, message us from Help & Support and a human replies.',
    ],
  },
];

const ParentDocsPage = () => {
  const [openId, setOpenId] = useState('getting-started');
  return (
    <ParentShell activeId="docs" title="Docs">
      <div className="px-6 lg:px-10 py-8 lg:py-10 space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white">
            <BookOpen size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Parent Documentation</h2>
            <p className="text-sm text-gray-500">Everything a parent or guardian needs, written in plain language.</p>
          </div>
        </div>

        <ul className="space-y-3" data-testid="parent-docs-list">
          {DOCS.map((d) => {
            const Icon = d.icon;
            const open = openId === d.id;
            return (
              <li key={d.id} className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenId(open ? null : d.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50"
                  data-testid={`parent-doc-toggle-${d.id}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition ${open ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
                    <Icon size={20} />
                  </div>
                  <span className="flex-1 font-bold text-gray-900">{d.title}</span>
                  <ChevronDown size={18} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-5 pb-6 pl-[80px] space-y-3" data-testid={`parent-doc-body-${d.id}`}>
                    {d.body.map((p, i) => (
                      <p key={i} className="text-sm text-gray-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </ParentShell>
  );
};

export default ParentDocsPage;
